import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { AuthService } from 'acl-cloud-lib';
import { BehaviorSubject, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
//import { RemoteDataMessage, AvailableSeriesData, MachineStructureData, RmtPropertyUpdates, SeriesUpdateData, ClientRemoteDataRequest, ClientFunctionCallRequest, SupplierStateMessage } from 'src/api/generated';
import { MachineOneService } from './machine-one.service';
import { AvailableSeriesData, ClientRemoteDataRequest, MachineStructureData, RemoteDataMessage, RmtPropertyUpdates, SeriesUpdateData, SupplierStateMessage, cast, getIRemoDataType } from './model';



/**
 * Enums of events to listen to.
 *
 * @enum {number}
 */
enum MessageEnum {
  REGISTERED = 'Registered',
  DATA = 'DataMessage'
}

/**
 * Enums of server methods, which can be invoked.
 *
 * @enum {number}
 */
enum ServerHubMethods {
  ENTER_ROOM = 'EnterMachineRoomAsClient',
  DATA_REQUEST = 'DataRequestFromClient',
  INVOKE_ON_SUPPLIER = 'InvokeOnSupplier',
  EXIT_ROOM = 'ExitMachineRoom'
}

export type RmtPropertySetterValue = string | boolean | number | string[] | boolean[] | number[];

/**
 * Class for communication with hub.
 * This class must to be single-instance per machine.
 *
 * @export
 * @class RemoteDataClientService
 */
export class RemoteDataClientService {

  /**
   * Instance of hub connection.
   *
   * @private
   * @type {HubConnection}
   * @memberof RemoteDataClientService
   */
  private hubConnection?: HubConnection;


  /**
   * Base hub url.
   *
   * @private
   * @type {string}
   * @memberof RemoteDataClientService
   */
  private _baseHubUrl: string = environment.baseHubUrl;

  /**
   * Name of machine.
   *
   * @private
   * @type {(string | null)}
   * @memberof RemoteDataClientService
   */
  private _currentMachineName: string | null = null;

  /**
   * Getter for machine name.
   *
   * @readonly
   * @type {string}
   * @memberof RemoteDataClientService
   */
  get currentMachineName(): string {
    return this._currentMachineName ?? "";
  }

  /**
   * Room enter observable information.
   *
   * @private
   * @type {BehaviorSubject<boolean>}
   * @memberof RemoteDataClientService
   */
  private _onRoomEnter: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _onRoomEnter$: Observable<boolean> = this._onRoomEnter.asObservable();

  /**
   * Available series received from hub.
   *
   * @private
   * @type {BehaviorSubject<IAvailableSeriesResponse>}
   * @memberof RemoteDataClientService
   */
  private _availableSeries: BehaviorSubject<AvailableSeriesData> = new BehaviorSubject<AvailableSeriesData>({ _t: "AvailableSeriesData", BarSeries: [], LineSeries: [] });
  public availableSeries$: Observable<AvailableSeriesData> = this._availableSeries.asObservable();

  /**
   * True/False = Activne/Not active
   *
   * @private
   * @type {BehaviorSubject<boolean>}
   * @memberof RemoteDataClientService
   */
  private _supplierState: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public supplierState$: Observable<boolean> = this._supplierState.asObservable();

  /**
   * Available machine structure from hub.
   *
   * @private
   * @type {Subject<RmtPropertyUpdates>}
   * @memberof RemoteDataClientService
   */
  // private _remotePropertyUpdates: Subject<RmtPropertyUpdates> = new Subject();
  // public remotePropertyUpdates$: Observable<RmtPropertyUpdates> = this._remotePropertyUpdates.asObservable();

  /**
   * Map of series with data observables and active connections count.
   * Different charts can refer to same data series.
   *  
   * @private
   * @type {Map<string, {data: Subject<SeriesUpdateData>, activeConnections: number}>}
   * @memberof RemoteDataClientService
   */
  private _activeSeriesWithDataObservables: Map<string, { data: Subject<SeriesUpdateData>, activeConnections: number }> = new Map<string, { data: Subject<SeriesUpdateData>, activeConnections: number }>();

  /**
   * Communication enabled flag.
   * Used for disable communication, when many changes are carried out.
   *
   * @private
   * @type {boolean}
   * @memberof RemoteDataClientService
   */
  private _communicationEnabled: boolean = true;

  /**
   * Storage for requested series.
   * Used for series comparision, to decide if new request is neccessary.
   * 
   * @private
   * @type {string[]}
   * @memberof RemoteDataClientService
   */
  private _lastSeriesRequested: string[] = [];

  /**
   * Reconnection interval, when connection fails.
   *
   * @private
   * @type {number}
   * @memberof RemoteDataClientService
   */
  private reconnectInterval: number = 5000;


  /**
   * Number of components watching to remote data updates.
   *
   * @private
   * @type {number}
   * @memberof RemoteDataClientService
   */
  private _watchRemoteDataUpdatesCounter: number = 0;

  /**
   * Service for working with machine structure
   *
   * @private
   * @type {MachineOneService}
   * @memberof RemoteDataClientService
   */
  private _machineOne: MachineOneService;

  /**
   * MachineOne getter
   *
   * @return {*}  {MachineOneService}
   * @memberof RemoteDataClientService
   */
  public getMachineOne(): MachineOneService {
    return this._machineOne;
  }

  constructor(private readonly _authService: AuthService) {
    this._machineOne = new MachineOneService();
  }


  /**
   * Method for starting connection, and initialization listeners for hub events.
   *
   * @memberof RemoteDataClientService
   */
  public startConnection() {
    this.hubConnection = this._buildConnection();

    this.hubConnection
      .start()
      .then(() => {
        console.log('Connection started');
        if (this._currentMachineName !== null) {
          this._enterRoom();
        }
      })
      .catch(err => {
        console.error('Error while starting connection: ' + err);
      });

    this.hubConnection?.on(MessageEnum.DATA, (data: RemoteDataMessage) => {
      switch (getIRemoDataType(data.Data)) {
        case 'AvailableSeriesData': {
          const converted = cast<AvailableSeriesData>(data.Data);
          if (converted) {
            this._availableSeries.next(converted);
          }
          break;
        }
        case 'MachineStructureData': {
          const converted = (cast<MachineStructureData>(data.Data))?.Structure ?? "{}";
          this._machineOne.initialize(converted);
          break;
        }
        case 'RmtPropertyUpdates': {
          const converted = cast<RmtPropertyUpdates>(data.Data);
          if (converted) {
            converted.Data.forEach(item => this._machineOne.updateVariable(Number(item.Id), item.Value));
          }
          break;
        }
        case 'SeriesUpdateData': {
          const d = cast<SeriesUpdateData>(data.Data);
          if (d) {
            this._activeSeriesWithDataObservables.get(d.SeriesName)?.data.next(d);
          }
          break;
        }
        case 'SupplierStateMessage': {
          const converted = cast<SupplierStateMessage>(data.Data);
          if (converted) {
            this._supplierState.next(converted.IsActive || false);
          }
          break;
        }
        default: throw Error(`Unsuported type ${data.Data._t}`)
      }
    });

    this.hubConnection.onclose((err) => {
      console.log('Connection closed', err);
      if (err) {
        this._tryReconnect();
        this._supplierState.next(false);
      }
    });

    this.hubConnection.onreconnecting((err) => {
      console.log('Connection reconecting', err);
    })
  }

  private _convertDatamessageToRequiredType<T>(data: any): T {
    if (environment.useMessagePackSignalR) {
      return data[1] as T;
    }
    const d = this._cleanData(data);
    //console.log(d);
    return d as T;
  }

  private _cleanData(data: any): any {
    if (Array.isArray(data)) {
      return data.map(item => this._cleanData(item));
    } else if (data && typeof data === 'object') {
      if (data.$values && Array.isArray(data.$values)) {
        // Ak obsahuje $values, vrátime pole po spracovaní každého prvku
        return data.$values.map((item: any) => this._cleanData(item));
      }

      // Destrukturovanie na odstránenie $type a $values, potom prechádzanie zvyšnými položkami
      const { $type, $values, ...rest } = data;
      return Object.fromEntries(
        Object.entries(rest).map(([key, value]) => [key, this._cleanData(value)])
      );
    }
    return data;
  }

  /**
   * Invokes function on supplier by function name.
   *
   * @param {string} functionName
   * @param {(any[] | null)} args
   * @return {*}  {Promise<boolean>}
   * @memberof RemoteDataClientService
   */
  public async invokeOnSupplier(functionName: string, args: any[] | null): Promise<boolean> {
    const func = this._machineOne.getFunctionByName(functionName);

    if (func) {
      if (this._machineOne.checkCallingFuncArguments(func, args)) {
        return (await this.hubConnection?.invoke<Partial<{ Success: boolean }>>(ServerHubMethods.INVOKE_ON_SUPPLIER, this._currentMachineName, func.id, args))?.Success || false;
      }
    }

    return false;
  }

  /**
   * Invokes function on supplier by id.
   *
   * @param {number} id
   * @param {(any[] | null)} args
   * @return {*}  {Promise<boolean>}
   * @memberof RemoteDataClientService
   */
  public async invokeOnSupplierById(id: number, args: any[] | null): Promise<boolean> {
    const func = this._machineOne.getFunctionById(id);

    if (func) {
      if (this._machineOne.checkCallingFuncArguments(func, args)) {
        return (await this.hubConnection?.invoke<Partial<{ Success: boolean }>>(ServerHubMethods.INVOKE_ON_SUPPLIER, this._currentMachineName, func.id, args))?.Success || false;
      }
    }

    return false;
  }

  /**
   * Sets property on machine.
   *
   * @param {number} id
   * @param {RmtPropertySetterValue} value
   * @return {*}  {Promise<boolean>}
   * @memberof RemoteDataClientService
   */
  public async setPropertyOnSupplier(id: number, value: RmtPropertySetterValue): Promise<boolean> {
    const setter = this._machineOne.getSetterById(id);

    if (setter) {
      return (await this.hubConnection?.invoke<Partial<{ Success: boolean }>>(ServerHubMethods.INVOKE_ON_SUPPLIER, this._currentMachineName, setter.id, [value]))?.Success || false;
    }

    return false;
  }

  /**
   * Method for client reconnection.
   *
   * @private
   * @memberof RemoteDataClientService
   */
  private _tryReconnect(): void {
    console.log(`Attempting to reconnect in ${this.reconnectInterval / 1000} seconds...`);

    setTimeout(async () => {
      if (this.hubConnection) {
        this.hubConnection
          .start()
          .then(() => {
            console.log('Reconnected successfully');
            if (this._currentMachineName !== null) {
              this._enterRoom().then((val) => {
                if (val) {
                  // Force to send data request after reconnect
                  this._criticalWatchChange = true;
                  this._sendDataRequest();
                }
              });
            }
            this.reconnectInterval = 5000;
          })
          .catch(err => {
            console.error('Reconnect failed: ' + err);
            if (this.reconnectInterval <= 60000) {
              this.reconnectInterval += 5000;
            }
            this._tryReconnect();
          });
      }
    }, this.reconnectInterval);
  }

  /**
   * Method for hub room enter.
   *
   * @private
   * @memberof RemoteDataClientService
   */
  private _enterRoom(): Promise<boolean> {
    return new Promise((resolve) => {
      this.hubConnection?.invoke(ServerHubMethods.ENTER_ROOM, this._currentMachineName)
        .then(() => {
          console.log('Room entered');
          resolve(true)
          this._onRoomEnter.next(true);
        })
        .catch(err => {
          resolve(false);
          console.error(err);
        });
    });
  }

  /**
   * Signal the machine/supplier to exit the room and stop sending data
   */
  public exitRoom(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.hubConnection) {
        resolve(false);
        return;
      }

      this.hubConnection.invoke(ServerHubMethods.EXIT_ROOM, this._currentMachineName)
        .then(() => {
          console.log(`Machine ${this._currentMachineName} signaled to exit room`);
          resolve(true);
        })
        .catch(err => {
          console.error('Error signaling machine to exit room:', err);
          resolve(false);
        });
    });
  }

  /**
   * Method for requesting new data series.
   * If given series were requested, just increment connection count in map.
   * Else initialize new record in map and request new series.
   * @param {string} series
   * @memberof RemoteDataClientService
   */
  public dataRequest(series: string) {
    if (this._activeSeriesWithDataObservables.has(series)) {
      this.incrementUsedConnections(series);
    } else {
      this._activeSeriesWithDataObservables.set(series, {
        data: new Subject(),
        activeConnections: 1
      });

      this._sendDataRequest();
    }
  }


  /**
   * Request new series from hub, by invoking it's method, if comminication is enabled.
   * 
   * @private
   * @memberof RemoteDataClientService
   */
  private _sendDataRequest(): void {
    const sub: Subscription = this._onRoomEnter$.subscribe((val: boolean) => {
      const tmp = Array.from(this._activeSeriesWithDataObservables.keys());
      if (val &&
        this._communicationEnabled &&
        this.hubConnection?.state == HubConnectionState.Connected &&
        (!this._arraysEqualUnique(this._lastSeriesRequested, tmp) || this._criticalWatchChange)
      ) {
        this._lastSeriesRequested = [...tmp];
        this._criticalWatchChange = false;

        const req: ClientRemoteDataRequest = { DataSeries: this._lastSeriesRequested, MachineDataUpdates: this._watchRemoteDataUpdatesCounter > 0 };
        console.log(new Date(), 'Sending request', req);
        this.hubConnection?.invoke(ServerHubMethods.DATA_REQUEST, req)
          .then(() => {
            sub.unsubscribe();
          })
          .catch(err => {
            console.error(err);
            sub.unsubscribe();
          });
      }
    });
  }

  /**
   * Flag for critical watch change. Indicates if request should be sent.
   *
   * @private
   * @type {boolean}
   * @memberof RemoteDataClientService
   */
  private _criticalWatchChange: boolean = false;

  /**
   * Sends data request with flag indication of remote data updates intereset.
   *
   * @param {boolean} watch
   * @memberof RemoteDataClientService
   */
  public watchRemoteDataUpdates(watch: boolean): void {
    if (watch) {
      if (this._watchRemoteDataUpdatesCounter == 0) {
        this._criticalWatchChange = true;
      }

      this._watchRemoteDataUpdatesCounter++;
    } else {
      if (this._watchRemoteDataUpdatesCounter > 0) {
        this._watchRemoteDataUpdatesCounter--;

        if (this._watchRemoteDataUpdatesCounter == 0) {
          this._criticalWatchChange = true;
        }
      }
    }

    this._sendDataRequest();
  }


  /**
   * Disconnecting client from hub, and removing all hub listeners.
   *
   * @memberof RemoteDataClientService
   */
  public disconnect() {
    if (this.hubConnection) {
      this.hubConnection.off(MessageEnum.REGISTERED);
      this.hubConnection.off(MessageEnum.DATA);

      this.hubConnection.stop()
        .then(() => {
          //this._onRoomEnter.next(false);
          console.log(`${this.currentMachineName} disconnected!`);
        });

    }
  }


  /**
   * Sets machine name for client, and starts connection.
   *
   * @param {(string | null)} name
   * @memberof RemoteDataClientService
   */
  public setCurrentMachineName(name: string | null): void {
    if (name !== null && name.trim().length > 0) {
      this._currentMachineName = name;
      this.startConnection();
    } else {
      if (this._onRoomEnter.value) {
        this._currentMachineName = null;
        this.disconnect();
      }
    }
  }


  /**
   * Increment used connection of given series.
   *
   * @param {string} series
   * @memberof RemoteDataClientService
   */
  public incrementUsedConnections(series: string): void {
    const record = this._activeSeriesWithDataObservables.get(series);
    if (record) {
      record.activeConnections++;
    }
  }

  /**
   * Decrement used connection of given series or remove it from map and request new data series.
   *
   * @param {string} series
   * @memberof RemoteDataClientService
   */
  public decrementUsedConnections(series: string): void {
    const record = this._activeSeriesWithDataObservables.get(series);
    if (record) {
      if (record.activeConnections > 1) {
        record!.activeConnections--;
      } else {
        this._activeSeriesWithDataObservables.delete(series);
        this._sendDataRequest();
      }
    }
  }

  /**
   * Disconnect client if no series are requested.
   *
   * @return {*}  {boolean}
   * @memberof RemoteDataClientService
   */
  public disconnectIfNotUsed(): boolean {
    if (this._activeSeriesWithDataObservables.size === 0) {
      this.disconnect();
      return true;
    }

    return false;
  }

  /**
   * Try to get data subject from map.
   *
   * @param {string} series
   * @return {*}  {Subject<IRemoteData>}
   * @memberof RemoteDataClientService
   */
  public getDataSubject(series: string): Subject<SeriesUpdateData> {
    const record = this._activeSeriesWithDataObservables.get(series);

    if (record === undefined) {
      throw Error(`There is no active connection for ${series}`);
    }

    return record.data;
  }

  /**
   * Compare 2 arrays by size and elements.
   *
   * @private
   * @template T
   * @param {T[]} arr1
   * @param {T[]} arr2
   * @return {*}  {boolean}
   * @memberof RemoteDataClientService
   */
  private _arraysEqualUnique<T>(arr1: T[], arr2: T[]): boolean {
    return new Set(arr1).size === new Set(arr2).size;
  }

  /**
   * Enable communication.
   *
   * @memberof RemoteDataClientService
   */
  public turnOnCommunictaion(): void {
    this._handleCommunicationEnabledChanges(true);
  }

  /**
   * Disable communication.
   *
   * @memberof RemoteDataClientService
   */
  public turnOffCommunication(): void {
    this._handleCommunicationEnabledChanges(false);
  }

  /**
   * Connection builder.
   *
   * @private
   * @return {*}  {HubConnection}
   * @memberof RemoteDataClientService
   */
  private _buildConnection(): HubConnection {
    const builder = new HubConnectionBuilder()
      .withUrl(`${this._baseHubUrl}`, {
        accessTokenFactory: () => this._authService.accessToken ?? ""
      })
      .configureLogging(LogLevel.Information);
    if (environment.useMessagePackSignalR) {
      builder.withHubProtocol(new MessagePackHubProtocol());
    }
    return builder.build();
  }

  /**
   * Handle changes of comminication enabled/disabled variable.
   *
   * @private
   * @param {boolean} val
   * @memberof RemoteDataClientService
   */
  private _handleCommunicationEnabledChanges(val: boolean): void {
    if (this._communicationEnabled !== val && val === true) {
      // rising edge
      // sever calls
      this._communicationEnabled = val;
      this._sendDataRequest();
    } else {
      this._communicationEnabled = val;
    }
  }
}
